import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Send, Smartphone, Monitor, Eye,
  Home, Play, DollarSign, Calendar, Users, MessageCircle,
  User, Trophy, Target, Sparkles, CheckCircle, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Email template configurations
const emailTemplates = {
  welcome: {
    id: 'welcome',
    name: 'Welcome Email',
    subject: 'Welcome to Par Seeker! Let\'s Get You Started',
    description: 'Sent to new users after registration'
  },
  game_invite: {
    id: 'game_invite', 
    name: 'Game Invitation',
    subject: 'You\'ve Been Invited to Play!',
    description: 'Sent when invited to Skins or Stroke Play'
  },
  settlement_reminder: {
    id: 'settlement_reminder',
    name: 'Settlement Reminder', 
    subject: 'You Have an Outstanding Settlement',
    description: 'Sent for unpaid game settlements'
  },
  weekly_digest: {
    id: 'weekly_digest',
    name: 'Weekly Handicap Update',
    subject: 'Your Weekly Golf Summary',
    description: 'Weekly stats and handicap update'
  }
};

// Welcome email content - Updated design
const welcomeEmailContent = {
  greeting: "Welcome to Par Seeker",
  tagline: "Golf is better when it's shared.",
  intro: "Par Seeker was built to bring golfers together — from casual weekend rounds to competitive skins games with your crew.",
  nowYouCan: "Now that you're here, you can:",
  features: [
    { icon: "🏌️", text: "Track rounds and scores" },
    { icon: "💬", text: "Connect with other golfers" },
    { icon: "🏆", text: "Create games and side bets" },
    { icon: "📊", text: "Build your golf history and stats" },
    { icon: "💰", text: "Settle games and payouts easily" },
    { icon: "📍", text: "Join groups and local golf communities" }
  ],
  startSection: {
    title: "Start Your First Round",
    subtitle: "The best way to experience Par Seeker is to jump in:",
    steps: [
      "Complete your profile",
      "Add your golf buddies",
      "Create or join a game",
      "Post your first round"
    ],
    closing: "Every round has a story.\nPar Seeker helps you keep it."
  },
  proTips: {
    title: "A Few Pro Tips",
    tips: [
      "Follow friends to see their rounds and activity",
      "Turn on notifications so you never miss a tee time or game invite",
      "Track your stats over time and watch your game improve",
      "Use groups to organize leagues, trips, and weekly games"
    ]
  },
  closing: {
    title: "Golf. Connected.",
    message: "Whether you're chasing birdies, settling skins, or just enjoying the walk — Par Seeker keeps your golf world in one place.",
    signoff: "We're glad you joined us.",
    team: "— Team Par Seeker",
    tagline: "Play Games. Settle Up. Build History."
  },
  footer: {
    company: 'Par Seeker',
    website: 'parseeker.com'
  }
};

// Icon component mapper
const IconComponent = ({ name, className }) => {
  const icons = {
    home: Home,
    play: Play,
    dollar: DollarSign,
    calendar: Calendar,
    users: Users,
    message: MessageCircle,
    user: User,
    trophy: Trophy,
    target: Target,
    sparkles: Sparkles,
    check: CheckCircle
  };
  const Icon = icons[name] || Target;
  return <Icon className={className} />;
};

// Email Preview Component - Updated Design
const EmailPreview = ({ template, viewMode, userName = "Golfer" }) => {
  const content = welcomeEmailContent;
  const logoUrl = '/images/parseeker-email-logo.png';
  
  return (
    <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${viewMode === 'mobile' ? 'max-w-[375px]' : 'max-w-[600px]'} mx-auto`}>
      {/* Email Header with Logo - White Background */}
      <div className="bg-white p-8 text-center border-b-4 border-[#1a472a]">
        <img 
          src={logoUrl} 
          alt="Par Seeker" 
          className="h-28 mx-auto"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>
      
      {/* Email Body */}
      <div className="p-6 bg-[#f8faf8]">
        {/* Welcome Title */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-[#1a472a] mb-2">
            {content.greeting}
          </h1>
          <p className="text-lg text-[#22c55e] font-medium italic">
            {content.tagline}
          </p>
        </div>
        
        {/* Intro */}
        <p className="text-gray-700 text-base leading-relaxed mb-6 text-center">
          {content.intro}
        </p>
        
        {/* Now You Can Section */}
        <p className="text-gray-700 font-medium mb-4">
          {content.nowYouCan}
        </p>
        
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-6">
          <ul className="space-y-3">
            {content.features.map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-gray-700">
                <span className="text-xl">{feature.icon}</span>
                <span>{feature.text}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Start Your First Round */}
        <div className="bg-[#1a472a] rounded-xl p-5 mb-6 text-white">
          <h2 className="text-xl font-bold mb-2">{content.startSection.title}</h2>
          <p className="text-green-200 text-sm mb-4">{content.startSection.subtitle}</p>
          <ol className="list-decimal list-inside space-y-2 mb-4">
            {content.startSection.steps.map((step, index) => (
              <li key={index} className="text-white">{step}</li>
            ))}
          </ol>
          <p className="text-green-200 italic text-sm whitespace-pre-line border-t border-green-700 pt-4 mt-4">
            {content.startSection.closing}
          </p>
        </div>
        
        {/* Pro Tips */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-[#1a472a] mb-3 text-center">
            {content.proTips.title}
          </h3>
          <ul className="space-y-2">
            {content.proTips.tips.map((tip, index) => (
              <li key={index} className="flex items-start gap-2 text-gray-600 text-sm">
                <span className="text-[#22c55e] mt-1">•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Divider */}
        <div className="border-t border-gray-200 my-6"></div>
        
        {/* Golf Connected Section */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-[#1a472a] mb-3">
            {content.closing.title}
          </h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            {content.closing.message}
          </p>
          <p className="text-gray-700 font-medium mb-1">
            {content.closing.signoff}
          </p>
          <p className="text-[#22c55e] font-semibold italic mb-4">
            {content.closing.team}
          </p>
          <p className="text-[#1a472a] font-bold text-lg">
            {content.closing.tagline}
          </p>
        </div>
        
        {/* Footer */}
        <div className="border-t border-gray-200 pt-4 text-center">
          <p className="text-gray-400 text-xs">
            © 2026 {content.footer.company} | {content.footer.website}
          </p>
          <p className="text-gray-400 text-xs mt-2">
            <a href="#" className="underline">Unsubscribe</a> | <a href="#" className="underline">Email Preferences</a>
          </p>
        </div>
      </div>
    </div>
  );
};

const EmailTemplatesPage = ({ user }) => {
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState('welcome');
  const [viewMode, setViewMode] = useState('desktop'); // 'desktop' or 'mobile'
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState(user?.email || '');
  
  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSending(true);
    try {
      const response = await fetch(`${API}/admin/email/send-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          template: selectedTemplate,
          to_email: testEmail,
          user_name: user?.name || 'Test User'
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Test email sent to ${testEmail}!`);
      } else {
        toast.error(data.detail || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSending(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-[var(--ps-bg-base)]">
      {/* Header */}
      <div className="bg-[var(--ps-bg-surface)] border-b border-[var(--ps-border)] px-4 py-3">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-[var(--ps-bg-hover)] rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[var(--ps-text-primary)]" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-[var(--ps-text-primary)]">Email Templates</h1>
            <p className="text-sm text-[var(--ps-text-secondary)]">Preview and customize email communications</p>
          </div>
        </div>
      </div>
      
      <div className="p-4 space-y-4">
        {/* Template Selector */}
        <Card className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)]">
          <CardContent className="p-4">
            <label className="block text-sm font-medium text-[var(--ps-text-secondary)] mb-2">
              Select Template
            </label>
            <div className="grid grid-cols-2 gap-2">
              {Object.values(emailTemplates).map((template) => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    selectedTemplate === template.id
                      ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                      : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)] hover:border-[var(--ps-primary)]/50'
                  }`}
                >
                  <p className={`font-medium text-sm ${
                    selectedTemplate === template.id 
                      ? 'text-[var(--ps-primary)]' 
                      : 'text-[var(--ps-text-primary)]'
                  }`}>
                    {template.name}
                  </p>
                  <p className="text-xs text-[var(--ps-text-muted)] mt-0.5 line-clamp-1">
                    {template.description}
                  </p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* View Mode Toggle & Test Send */}
        <Card className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-[var(--ps-text-secondary)]" />
                <span className="text-sm font-medium text-[var(--ps-text-primary)]">Preview Mode</span>
              </div>
              <div className="flex bg-[var(--ps-bg-elevated)] rounded-lg p-1">
                <button
                  onClick={() => setViewMode('desktop')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'desktop' 
                      ? 'bg-[var(--ps-primary)] text-white' 
                      : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
                  }`}
                >
                  <Monitor className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('mobile')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'mobile' 
                      ? 'bg-[var(--ps-primary)] text-white' 
                      : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Test Email */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ps-text-muted)]" />
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Enter email for test"
                  className="w-full pl-10 pr-4 py-2 bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] rounded-lg text-[var(--ps-text-primary)] text-sm focus:outline-none focus:border-[var(--ps-primary)]"
                />
              </div>
              <Button
                onClick={handleSendTest}
                disabled={sending || !testEmail}
                className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white"
              >
                {sending ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Test
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Email Preview */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-[var(--ps-text-secondary)]">
              Preview: {emailTemplates[selectedTemplate]?.name}
            </h3>
            <span className="text-xs text-[var(--ps-text-muted)] bg-[var(--ps-bg-elevated)] px-2 py-1 rounded">
              Subject: {emailTemplates[selectedTemplate]?.subject}
            </span>
          </div>
          
          <div className={`bg-gray-200 rounded-xl p-4 ${viewMode === 'mobile' ? 'flex justify-center' : ''}`}>
            {selectedTemplate === 'welcome' ? (
              <EmailPreview 
                template={selectedTemplate} 
                viewMode={viewMode}
                userName={user?.name?.split(' ')[0] || 'Golfer'}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-[600px] mx-auto">
                <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">
                  {emailTemplates[selectedTemplate]?.name}
                </h3>
                <p className="text-gray-500">
                  Template coming soon. Build this template next?
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Info Note */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <p className="text-sm text-blue-400">
            <strong>Note:</strong> This email will be automatically sent to new users when they register. 
            Test it by sending to yourself first.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatesPage;
